// validationRules.js
// Utility: parse rules and validate a single field value
import dayjs from "dayjs";
import { getStoredFormData, getUserNRIDetails } from "../../utils/formStorage";
import { DMQStepNames } from "../../constants/constants";

/**
 * Extract maxLength from rules array.
 * rules example: ["mandatory", "maxLength:200", "alphaNumeric"]
 * returns integer or null
 */
export function extractMaxLength(rules = []) {
  if (!Array.isArray(rules)) return null;
  const match = rules.find((r) => typeof r === "string" && r.startsWith("maxLength:"));
  if (!match) return null;
  const parts = match.split(":");
  const num = parseInt(parts[1], 10);
  return Number.isFinite(num) ? num : null;
}

export function extractMinMax(rules = []) {
  if (!Array.isArray(rules)) return {};

  let min = null,
    max = null;

  rules.forEach((rule) => {
    if (typeof rule === "string" && rule.startsWith("min:")) {
      const v = parseInt(rule.split(":")[1], 10);
      if (!isNaN(v)) min = v;
    }
    if (typeof rule === "string" && rule.startsWith("max:")) {
      const v = parseInt(rule.split(":")[1], 10);
      if (!isNaN(v)) max = v;
    }
  });

  return { min, max };
}

/**
 * Validate a single field against its rules.
 * Returns null if valid, otherwise a user-friendly error message string.
 *
 * Supported rules:
 *  - "mandatory"            => "This field is required"
 *  - "emailRule"            => "Enter a valid email address"
 *  - "minLength:N"          => "Minimum N characters required"
 *  - "maxLength:N"          => "Maximum N characters allowed"
 *  - "alphaNumeric"         => only letters + numbers + spaces + ,/.
 *  - "alphaNumericStrict"   => only letters + numbers
 *  - "numeric"              => only digits
 *  - "numeric-float"            => only digits + optional decimal point
 *  - "alphabetic"           => only letters + spaces
 *  - "pincode"              => min requirement of 6 digits
 */
export function validateFieldRules(value, field = {}) {
  const rules = Array.isArray(field.rules) ? field.rules : [];

  // Skip validation if this field is hidden for current gender

  // treat empty (null, undefined, "", []) as empty
  const isEmpty = (v) => v === undefined || v === null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && v.length === 0);

  // mandatory
  if (rules.includes("mandatory") && isEmpty(value)) {
    return "This field is required";
  }

  // If empty and not mandatory -> skip other validations
  if (isEmpty(value)) return null;

  // Convert value to string for pattern checks (if array convert first element)
  let valStr = value;
  if (Array.isArray(value)) valStr = value[0] ?? "";
  if (typeof valStr !== "string") valStr = String(valStr);

  // 2) minLength
  const minLengthRule = rules.find((r) => typeof r === "string" && r.startsWith("minLength:"));
  if (minLengthRule) {
    const [, ruleLimit] = minLengthRule.split(":");
    const num = parseInt(ruleLimit, 10);

    if (Number.isFinite(num) && valStr.length < num) {
      return `Minimum ${num} characters required`;
    }
  }

  // 3) maxLength
  const maxLengthRule = rules.find((r) => typeof r === "string" && r.startsWith("maxLength:"));
  if (maxLengthRule) {
    const [, ruleLimit] = maxLengthRule.split(":");
    const defaultLimit = parseInt(ruleLimit, 10);

    let num = defaultLimit;

    if (field.question === "Pincode") {
      const isUserNRI = getUserNRIDetails();
      if (isUserNRI) num = 13;
    }

    if (Number.isFinite(num) && valStr.length > num) {
      return `Maximum ${num} characters allowed`;
    }
  }

  // 4) alphaNumeric
  // if (rules.includes("alphaNumeric")) {
  //   // allow letters, numbers, spaces, comma, dot, slash
  //   const alphaNumericRegex = /^[A-Za-z0-9\s.,/]*$/;

  //   if (!alphaNumericRegex.test(valStr)) {
  //     return "Only letters, numbers, spaces, comma, dot and slash are allowed";
  //   }
  // }

  if (rules.includes("alphaNumeric")) {
    // block only: { } < > `
    const blockedRegex = /[{}<>`]/;

    if (blockedRegex.test(valStr)) {
      return "Characters { } < > ` are not allowed";
    }
  }

  // emailRule
  if (rules.includes("emailRule")) {
    const emailRegex = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

    if (!emailRegex.test(valStr)) {
      return "Enter a valid email address";
    }
  }

  if (rules.includes("passportStrict")) {
    const passportStrictRegex = /^[A-Za-z][A-Za-z0-9]{7,14}$/;

    if (!passportStrictRegex.test(valStr)) {
      return "Please enter a valid passport number";
    }
  }

  if (rules.includes("alphaNumericStrict")) {
    // First block dangerous characters
    const blockedRegex = /[{}<>`]/;

    if (blockedRegex.test(valStr)) {
      return "Characters { } < > ` are not allowed";
    }

    // Allow A-Z, a-z, 0-9 and single spaces between words
    const alphaNumericStrictRegex = /^[A-Za-z0-9]+(?: [A-Za-z0-9]+)*$/;

    if (!alphaNumericStrictRegex.test(valStr)) {
      return "Only letters and numbers are allowed";
    }
  }
  // 4) numeric / numberic (some schemas may have typo 'numberic' — support both)
  // numeric rule
  if (rules.includes("numeric")) {
    if (!/^[0-9]+$/.test(valStr)) return "Only numbers are allowed";

    // --- NEW: apply min/max when numeric ---
    const { min, max } = extractMinMax(rules);
    const numVal = parseInt(valStr, 10);

    if (Number.isFinite(numVal)) {
      if ((min !== null && numVal < min) || (max !== null && numVal > max)) {
        return `Value must be between ${min} and ${max}`;
      }
    }
  }

  // numeric-float rule
  if (rules.includes("numeric-float")) {
    // allows:
    // 123
    // 123.45
    // 0.5
    // 10.0

    if (rules.includes("numeric-float")) {
      if (!/^\d+(\.\d+)?$/.test(valStr)) {
        return "Only numbers and a single decimal point are allowed";
      }
    }

    const { min, max } = extractMinMax(rules);
    const numVal = parseFloat(valStr);

    if (Number.isFinite(numVal)) {
      if ((min !== null && numVal < min) || (max !== null && numVal > max)) {
        return `Value must be between ${min} and ${max}`;
      }
    }
  }

  // 5) alphabetic
  if (rules.includes("alphabetic")) {
    if (!/^[A-Za-z\s]+$/.test(valStr)) return "Only letters are allowed";
  }

  // 6) pincode - must be exactly 6 digits
  if (rules.includes("pincode")) {
    if (!/^\d{6}$/.test(valStr)) {
      return "Please enter a 6 digit pincode";
    }
  }

  return null;
}

export const formatToDDMMYYYY = (date) => {
  if (!date) return "";
  return dayjs(date).format("DD/MM/YYYY");
};

export const parseDDMMYYYY = (str) => {
  if (!str) return null;
  return dayjs(str, "DD/MM/YYYY");
};
